/** Текст политики обработки данных.
 *
 *  Артефакт репозитория, а не ссылка наружу: текст, на который ссылается
 *  согласие при регистрации, обязан быть доступен без сети и обязан
 *  меняться вместе с кодом, который он описывает. Расхождение здесь того же
 *  рода, что расхождение спеки с кодом, только дороже: там следующий
 *  исполнитель чинит код обратно к сломанному, а здесь родитель соглашается
 *  не с тем, что происходит на самом деле.
 *
 *  Каждое утверждение ниже проверяемо по коду. Если меняется поведение —
 *  меняется и абзац, в тот же коммит. */
export const POLICY = {
  title: 'Политика обработки данных',
  updated: '2026-09-15',
  sections: [
    {
      heading: 'Коротко',
      paragraphs: [
        'Имени ребёнка в приложении нет нигде. Профиль — это цвет и зверёк: «синий ёж», «зелёная лиса». Свободного текста, который вводит пользователь, у профиля нет вовсе.',
        'Заниматься можно без всякой учётной записи. Тогда занятия хранятся только на этом устройстве, никуда не отправляются, и наружу о ребёнке не уходит ничего, кроме сообщений об ошибках приложения.',
        'Если взрослый заводит учётную запись, от него нужна только почта: она нужна, чтобы вернуть доступ к занятиям, если пароль забыт, и чтобы занятия были видны на втором устройстве.',
        'Рекламы, счётчиков посещаемости и передачи данных рекламным сетям нет. Занятия детей не продаются и не передаются никому, кроме перечисленных ниже поставщиков услуг.',
      ],
    },
    {
      heading: 'Кто обрабатывает данные и как связаться',
      paragraphs: [
        'Данные обрабатывает владелец приложения mathpaw.com. Написать ему можно на privacy@mathpaw.com — по любому вопросу об этих данных, включая требование их удалить. Это единственный адрес для связи, и ответ приходит от человека, а не от службы поддержки.',
      ],
    },
    {
      heading: 'Что собирается',
      paragraphs: [
        'Об учётной записи взрослого: адрес почты и зашифрованный пароль. Их хранит служба входа Supabase.',
        'О профиле ребёнка: цвет, зверёк и время создания. Ни имени, ни возраста, ни класса, ни фотографии.',
        'О занятиях: какой пример был показан, что ребёнок ответил, верно ли, сколько времени занял ответ, отвлекался ли он на другое приложение посреди задания, и сводка по каждой тренировке. По этим записям считается освоенность — то, что видно на экране «Успехи».',
        'Об устройстве: случайный идентификатор устройства, чтобы записи двух планшетов не перепутались при объединении.',
      ],
    },
    {
      heading: 'Чего не собирается',
      paragraphs: [
        'Имя, фамилия, возраст, пол, школа, класс, фотографии, голос, список контактов, местоположение. Ничего этого приложение не спрашивает и не может получить.',
        'Нет рекламных идентификаторов, пикселей и сторонних счётчиков.',
      ],
    },
    {
      heading: 'Где это лежит',
      paragraphs: [
        'Основное место — устройство ребёнка: занятия пишутся в хранилище браузера и работают без интернета.',
        'Пока взрослый не вошёл, это единственное место: копии нет нигде.',
        'После входа копия хранится в базе Supabase в Германии (регион eu-central-1, Европейский союз). Она нужна, чтобы занятия были видны на втором устройстве и пережили потерю планшета.',
        'Правило доступа записано в самой базе: строки одной семьи не отдаются другой. Это не проверка в приложении, которую можно обойти, а правило на стороне сервера.',
      ],
    },
    {
      heading: 'Кому данные передаются',
      paragraphs: [
        'Supabase (Ирландия, серверы в Германии) — хранение базы и вход по почте и паролю.',
        'Brevo (Франция) — отправка писем: подтверждение адреса, код для смены пароля и ответы на письма, которые вы пишете на privacy@mathpaw.com. Для писем входа ему передаётся только адрес почты взрослого, а ответ на ваше письмо проходит через него целиком.',
        'Sentry (Европейский союз, приём в Германии) — сообщения об ошибках приложения. Что именно туда уходит, описано ниже отдельно.',
        'Netlify (США) — раздача самих файлов приложения. Cloudflare (США) — доменные имена и пересылка писем, которые вы пишете на privacy@mathpaw.com: такое письмо проходит через Cloudflare и хранится в почтовом ящике владельца у Google (США). Занятия им не передаются.',
        'Сетевой адрес устройства (IP) видят Netlify, Supabase и Sentry: приложение обращается к ним напрямую, так устроен любой запрос в интернете, и скрыть его нельзя. Мы его не храним у себя и не используем ни для чего, но у этих поставщиков он остаётся в их служебных журналах на их сроки. К Brevo, Cloudflare и Google приложение с устройства не обращается.',
      ],
    },
    {
      heading: 'Что уходит в сообщения об ошибках',
      paragraphs: [
        'Когда приложение ломается, в Sentry уходит техническое описание поломки: в каком месте кода она случилась и что делал экран.',
        'Вместе с ним уходит повтор последних секунд вокруг ошибки. Это не видеозапись, а поток изменений страницы: что появлялось на экране, куда нажимали и с какими задержками. Весь текст в нём заменён заглушками, картинки не передаются, поля пароля закрыты отдельно.',
        'Стоит сказать прямо: по такому повтору видно, как ребёнок вёл себя в те секунды — куда нажал, сколько думал. Это цена возможности починить поломку, случившуюся у него на экране. Повторы пишутся только вокруг ошибок: при обычном занятии не записывается ничего.',
        'Содержимое журнала занятий, освоенность и история тренировок в сообщения об ошибках не попадают. Адреса запросов очищаются от одноразовых кодов из писем и от идентификаторов профилей до того, как уйти.',
        'Отдельно передаются замеры скорости загрузки — они к ошибкам отношения не имеют и содержимого занятий не несут.',
      ],
    },
    {
      heading: 'Приложение на устройстве',
      paragraphs: [
        'Приложение можно поставить на домашний экран телефона или планшета — если браузер это умеет; почти все умеют, но не все. Тогда оно открывается как обычное приложение и работает без интернета, начиная со второго запуска: при самом первом кешировать ещё нечего. Никаких дополнительных прав при этом не запрашивается: ни камеры, ни микрофона, ни уведомлений, ни местоположения, ни списка контактов.',
        'Чтобы это было возможно, браузер хранит файлы самого приложения — разметку, оформление и код — в своём кеше. Занятий и почты там нет: они лежат в отдельном хранилище и в кеш приложения не попадают.',
        'Приложение ничего не делает в фоне: пока его не открыли, оно не работает, никуда не обращается и уведомлений не шлёт.',
        'Стереть это можно очисткой данных сайта в настройках браузера — это работает везде. Удаление значка с домашнего экрана на части устройств тоже стирает всё, а на части оставляет данные браузеру; полагаться на него как на способ стирания не стоит.',
      ],
    },
    {
      heading: 'Кому принадлежат занятия на этом устройстве',
      paragraphs: [
        'Устройство помнит, за какой учётной записью оно закреплено. Пока не закреплено ни за какой — занятия ничьи и достанутся той записи, в которую взрослый войдёт. Если на устройстве уже есть занятия, приложение об этом прямо предупреждает и называет почту, и ждёт согласия; на пустом устройстве переносить нечего, и оно закрепляется молча.',
        'Если войти в другую запись, занятия прежней не показываются и с новыми не смешиваются. Их можно только стереть, и только по прямому нажатию взрослого.',
      ],
    },
    {
      heading: 'Сколько это хранится',
      paragraphs: [
        'Бессрочно — пока вы сами не удалите. Это осознанное решение: ребёнок возвращается к таблице умножения через месяцы, и стирать его прогресс по сроку значило бы обнулять то, ради чего всё и сделано.',
        'Удалённое исчезает из базы сразу, но какое-то время остаётся в резервных копиях Supabase — до тридцати дней. Оттуда оно не читается приложением и удаляется вместе с истечением копии.',
        'Сообщения об ошибках и повторы сессий Sentry хранит до девяноста дней, после чего удаляет сам.',
      ],
    },
    {
      heading: 'Что вы можете сделать',
      paragraphs: [
        'Выгрузить занятия ребёнка в файл: раздел «Родителям» → «Выгрузить занятия». Файл читаемый, его же можно загрузить обратно.',
        'Удалить профиль ребёнка вместе со всеми его занятиями: «Родителям» → «Удалить навсегда». С устройства они стираются сразу и необратимо. Из облака — при ближайшей синхронизации; приложение прямо говорит, случилось это уже или ещё нет. Если вы не заводили учётную запись, облачной копии и нет.',
        'Удалить учётную запись целиком: «Родителям» → «Удалить учётную запись». Удаляются почта взрослого, все профили детей и все занятия.',
        'Стереть занятия только с этого устройства: выйти из учётной записи. Отправленное вернётся при следующем входе.',
        'Если вы не заводили учётную запись — стереть всё разом можно очисткой данных сайта в настройках браузера. Копии в этом случае нет нигде, и восстановить будет нечем.',
        'Написать на privacy@mathpaw.com и попросить сделать любое из этого.',
      ],
    },
    {
      heading: 'О детях и о согласии',
      paragraphs: [
        'Учётная запись принадлежит взрослому. Заводя профиль ребёнка, взрослый подтверждает, что он родитель или законный представитель этого ребёнка, и разрешает обработку данных его занятий на условиях этой политики.',
        'Собственной учётной записи у ребёнка нет, и никакого способа связаться с ним у приложения тоже нет: ни почты, ни сообщений, ни ленты.',
      ],
    },
    {
      heading: 'Изменения',
      paragraphs: [
        'Дата редакции стоит вверху. Существенные изменения будут показаны при входе, а не спрятаны в новой редакции.',
      ],
    },
  ],
};
